import React, { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import { textLang } from 'lowcomp/plugins/member/common/textLang';
import { getLang, showErrorMessage } from 'lowcomp/plugins/member/utils';
import { useEcoStore } from 'lowcode/utils/context/ecocredits';
import { injectNotes } from 'lowcomp/plugins/member/utils';
import RuleModal from 'lowcomp/plugins/member/common/RuleModal';
import NoAward from 'lowcomp/plugins/member/common/NoAward';
import CouponModal from 'lowcomp/plugins/member/common//CouponModal';
import EditorImg from 'lowcode/components/EditorImg';
import EditorLink from 'lowcode/components/EditorLink';
import { useRouter } from 'next/router';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import isEmpty from 'lodash/isEmpty';
import styled from './index.module.scss';
import FlopGame from './FlopGame';
import { useLuckyStore, useLuckyDispatch } from './LuckyProvider';
import { checkRewardDraw, rewardDraw } from '@/service/vip';
import getLocal from '@/app/utils/getLocal';
import EFArrowRight from '@/components/UI/svg/Icons/EFArrowRight';
import { signupCookie } from '@/utils/cookies/signupCookie';
import gtm from '@/utils/gtm';
import { createPortal } from 'react-dom';
import LoginFormModal from '@/components/LoginFormModal';

interface LuckyTurntableProps {
    id: string;
    isEditStatus: boolean;
    isLogin: boolean;
    setShowPage: (page: string) => void;
    refershData?: () => void;
    imgConfig: {
        bgImage: string;
        mobileBgImage: string;
        kvImage: string;
        frontImage: string;
        backImage: string;
    };
}

// 添加卡片状态
type IFlopItem = {
    id: React.Key;
    title: string;
    imgUrl?: string;
    checked?: boolean; // 是否选中
    flip?: boolean; //是否翻转
    x: number; //
    y: number;
    type?: string;
};

interface Prizes {
    drawId: React.Key; // 抽奖活动ID
    id: React.Key;
    isDisplayName: boolean;
    prizeId: React.Key; // 奖品ID
    prizeName: string;
    tips: string;
    type: 'NOTHING' | 'INTEGRAL' | 'COUPONS' | 'FREE_PRODUCTS' | 'THIRD_COUPONS';
    iconUrl?: string;
    isDisplayIcon?: boolean;
    loginType?: 'page' | 'modal';
}

// 三种模式偏移量有区别
const offsetY: Record<number, number> = {
    1: 0,
    2: 120,
    3: 140,
};

const offsetX = 244;

const LuckyTurntable: React.FC<LuckyTurntableProps> = ({ id, isEditStatus, isLogin, setShowPage, refershData, imgConfig, loginType = 'page' }) => {
    const dispatch = useLuckyDispatch();
    const { LUCKY_ACTIVE: luckyActivety } = useLuckyStore();
    const { inBlackList } = useEcoStore();
    const router = useRouter();
    const { locale, pathname } = getLocal();
    const t = getLang(textLang, locale);
    const [currNotes, setCurrNotes] = useState<any>();
    const [winnerPrize, setWinnerPrize] = useState<any>();
    const [isValidTime, setIsValidTime] = useState(true);
    const timer = useRef<number>();
    const pathUrl = `/${locale}/ecocredits`;
    const [flopList, setFlopList] = useState<IFlopItem[]>([]);
    const [awardData, setAwardData] = useState<Prizes | null>(null);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const rowsModal = Math.ceil(luckyActivety?.drawAwards?.length / 3); // 有几排产品（最多3排9张）

    const { timezoneList } = useMemberTimeStore();

    const [status, setStatus] = useState<1 | 2 | 3>(1); // 1 准备 2 抽奖 3 结束
    const disabledGame = inBlackList || !isValidTime || (luckyActivety?.limitFlag && !luckyActivety?.available);

    // debug 类型3
    // if (luckyActivety) luckyActivety.playWays = 3;

    const getRemainingTimesText = () => {
        const { limitCount, limitDay, playWays } = luckyActivety || {};

        // 活动期间
        if (limitDay === -1) {
            if (playWays !== 1) {
                return t.rich('limit_times_in_active2', { count: () => <i className="remaining-times-count">{limitCount || 0}</i> });
            }
            return t.rich('limit_times_in_active', { count: () => <i className="remaining-times-count">{limitCount || 0}</i> });
        }

        // 不限时间参与
        if (limitDay === 1000000000) {
            if (playWays !== 1) {
                return t.rich('without_limit_times2', { count: () => <i className="remaining-times-count">{limitCount || 0}</i> });
            }
            return t.rich('without_limit_times', { count: () => <i className="remaining-times-count">{limitCount || 0}</i> });
        }

        const day = Math.round(limitDay / 60 / 24);
        // 韩法单天文案调整
        if (locale === 'kr' && day <= 1) {
            return t.rich('limit_times_in_day_one', {
                count: () => <i className="remaining-times-count">{limitCount || 0}</i>,
            });
        }
        if (locale === 'fr' && day <= 1) {
            return t.rich('limit_times_in_day_one', {
                count: () => <i className="remaining-times-count">{limitCount || 0}</i>,
                day: () => <i className="remaining-times-count">{day}</i>,
            });
        }
        if (playWays !== 1) {
            return t.rich('limit_times_in_day2', {
                count: () => <i className="remaining-times-count">{limitCount || 0}</i>,
                day: () => <i className="remaining-times-count">{day}</i>,
            });
        }
        return t.rich('limit_times_in_day', {
            count: () => <i className="remaining-times-count">{limitCount || 0}</i>,
            day: () => <i className="remaining-times-count">{day}</i>,
        });
    };

    const openRuleModal = () => {
        const ruleList = luckyActivety?.description?.split('\n')?.filter((str: string) => !!str);
        setCurrNotes(ruleList || []);
    };

    const getIsValidTime = () => {
        if (!luckyActivety) return;
        const { startTime, endTime } = luckyActivety;
        const startTimestamp = Number(startTime);
        const endTimestamp = Number(endTime);

        // 不能转换成时间戳，不做处理
        if (Number.isNaN(startTimestamp) || Number.isNaN(endTimestamp)) {
            return;
        }

        const now = new Date().valueOf();
        const newIsValidTime = startTimestamp < now && endTimestamp > now;
        if (isValidTime !== newIsValidTime) {
            setIsValidTime(newIsValidTime);
        }
    };

    const handleAddNotes = (coupon: any) => {
        if (!coupon) return;
        if (coupon.ruleType === 'COUPONS') {
            return injectNotes([coupon], t, locale, timezoneList)[0];
        }

        if (coupon?.ruleDescription) {
            const noteList = coupon?.ruleDescription?.split('\n')?.filter((str: string) => !!str);
            return {
                ...coupon,
                noteList,
            };
        }
    };

    const initFlopList = () => {
        const initFlopArr = luckyActivety?.drawAwards?.map((item: Prizes, index: number) => {
            const flopItem = {
                id: item.id as string,
                checked: false,
                flip: false,
                // title: '',
                title: item.prizeName,
                imgUrl: '',
                x: 0,
                y: 0,
            };
            const row = Math.ceil((index + 1) / 3);
            const column = (index + 1) % 3;
            // y偏移量
            if (row === 1) {
                flopItem.y = offsetY[rowsModal];
            }
            if (row === 2) {
                flopItem.y = 0;
            }
            if (row === 3) {
                flopItem.y = -offsetY[rowsModal];
            }
            // x偏移量
            if (column === 1) {
                flopItem.x = offsetX;
            } else if (column === 2) {
                flopItem.x = 0;
            } else {
                flopItem.x = -offsetX;
            }
            return flopItem;
        });
        setFlopList(initFlopArr);
    };

    const handleStart = () => {
        if (!isLogin) {
            signupCookie?.({
                sector_name: luckyActivety?.title,
                entry_position: 0,
            });

            if (loginType === 'modal') {
                gtm.push({
                    event: 'uaEvent',
                    event_name: 'activity_join_login',
                    button_name: `${luckyActivety?.title}_${t('lucky_flop_start')}`,
                });
                setShowLoginModal(true);
                return;
            }
            router.push(`/${locale}/login?redirect=${window.location.href}`);
            return;
        }
        if (disabledGame) return;
        setStatus(2);
        setAwardData(null);
        setTimeout(() => {
            const arr = [...flopList];
            arr.map((item) => {
                item.x = 0;
                item.y = rowsModal === 1 ? offsetY[rowsModal] : 0;
            });
            setFlopList(arr);
        }, 0);
    };

    const handleAccept = () => {
        initFlopList();
        let obj = awardData;
        if (!obj) {
            obj = luckyActivety?.drawAwards?.find((item: Prizes) => item.type === 'NOTHING') as Prizes;
        }
        handleEnd({ ...obj } || {});
        setTimeout(() => {
            setStatus(1);
        }, 500);
    };

    const handleEnd = (winnerPrize: any) => {
        if (winnerPrize) {
            setWinnerPrize(winnerPrize);
        }

        dispatch({
            type: 'getActives',
            payload: { isEditStatus, locale, id },
        });
        refershData?.();
    };
    const [loading, setLoading] = useState(false);

    const handleSettlement = (targetId: React.Key, targetIndex: number) =>
        new Promise((resolve, _reject) => {
            const targetPrize = luckyActivety?.drawAwards?.find((item: Prizes) => item.id === targetId) as any; // 抽中的 因为返回的对象属性不全
            const otherPrizes = luckyActivety?.drawAwards?.filter((item: Prizes) => item.id !== targetId); // 未抽中的其他奖品
            let otherPrizeIndex = 0; // 其他产品的索引

            const newFlopList = [...flopList];
            newFlopList.map((item, index) => {
                if (index === targetIndex) {
                    item.title = targetPrize.isDisplayName ? targetPrize.prizeName : '';
                    item.imgUrl = targetPrize.isDisplayIcon ? targetPrize.iconUrl : '';
                    item.checked = true;
                    item.flip = true;
                    item.type = targetPrize.type;
                } else {
                    item.title = otherPrizes[otherPrizeIndex].isDisplayName ? otherPrizes[otherPrizeIndex].prizeName : '';
                    item.imgUrl = otherPrizes[otherPrizeIndex].isDisplayIcon ? otherPrizes[otherPrizeIndex].iconUrl : '';
                    otherPrizeIndex++;
                }
            });
            setFlopList(newFlopList);
            setTimeout(() => {
                const arr = [...flopList];
                arr.map((item) => {
                    item.flip = true;
                });
                setFlopList(arr);
                resolve(arr);
            }, 500);
        });

    const onCheckError = () => {
        dispatch({
            type: 'getActives',
            payload: { isEditStatus, locale, id },
        });
        refershData?.();
    };

    const handleChoose = async (targetIndex: number) => {
        if (status !== 2) return;
        if (loading) return;

        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'floating_button',
                button_name: 'OPEN',
            });
        } catch (e) {
            console.error('e', e);
        }

        setLoading(true);
        setAwardData(null);

        // check
        const { id: drawId, version } = luckyActivety;
        try {
            const checkRes = await checkRewardDraw({ drawId, version }, locale, pathUrl);
            if (checkRes?.code !== '0') {
                setLoading(false);
                showErrorMessage(checkRes?.code, t);
                onCheckError();
                return;
            }
        } catch (e) {
            message.error(t('network_error'));
            setLoading(false);
            onCheckError();
            console.error('e', e);
            return;
        }

        // 获取奖项id
        const prizeRes = await rewardDraw({ drawId, version }, locale, pathUrl);
        if (prizeRes?.code === '0') {
            prizeRes.data && (await handleSettlement(prizeRes.data.awardsId, targetIndex));
            setLoading(false);
            if (prizeRes.data.prizeType === 'NOTHING') {
                setStatus(3);
                setAwardData(null);
            } else {
                setStatus(3);
                setAwardData(prizeRes.data);
            }
            // setStatus(3);
            // setAwardData(prizeRes.data);
        } else {
            // 找到未中奖的奖项
            const data = luckyActivety?.drawAwards?.find((item: Prizes) => item.type === 'NOTHING');
            data && (await handleSettlement(data.id, targetIndex));
            showErrorMessage(prizeRes?.code, t);
            setLoading(false);

            setStatus(3);
            setAwardData(null);
            // handleAccept();
            // onEnd?.(data);
        }
    };

    useEffect(() => {
        if (!luckyActivety?.drawAwards || status === 3) return;
        initFlopList();
    }, [luckyActivety?.drawAwards]);

    useEffect(() => {
        timer.current = window.setInterval(getIsValidTime, 500);
        return () => {
            timer.current && window.clearInterval(timer.current);
        };
    }, [luckyActivety, isValidTime]);

    useEffect(() => {
        if (id) {
            dispatch({
                type: 'getActives',
                payload: { isEditStatus, locale, id },
            });
        }
        if (!id && isEditStatus) {
            dispatch({
                type: 'GET_ACTIVE_LIST_TYPES',
                payload: {
                    title: '模块主标题',
                    subTitle: '模块副标题',
                },
            });
        }
    }, [id]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    }, []);

    if (!isEditStatus && isEmpty(luckyActivety)) return <></>;

    return (
        <>
            <div className={`${styled.luckyFlopgame}`}>
                {imgConfig.bgImage && <EditorImg className="flopgame_bg" pc={imgConfig.bgImage} h5={imgConfig.mobileBgImage} />}

                <div
                    className="lucky-content"
                    style={
                        imgConfig.bgImage
                            ? {
                                  transform: 'translateY(-100%)',
                              }
                            : {}
                    }
                >
                    <div className="lucky-left">
                        <h5 className="lucky-title">{luckyActivety?.title}</h5>
                        <div>
                            <p className="lucky-desc">{luckyActivety?.subTitle}</p>
                            {!!luckyActivety?.limitFlag && <p className="lucky-limit-rule">{getRemainingTimesText()}</p>}

                            {luckyActivety?.type === 2 && (
                                <p className="lucky-limit-rule">{t('join_lucky_with_points2', { points: luckyActivety?.usePoints || 0 })}</p>
                            )}
                        </div>
                        {!!luckyActivety?.description && (
                            <div onClick={openRuleModal} className="lucky-rule-more">
                                {t('learn_lucky_rule')}
                                <EFArrowRight />
                            </div>
                        )}

                        {/* mobile: 剩余抽奖次数 */}

                        <div className={`flop_tools ${status === 2 ? 'hide' : ''}`}>
                            {luckyActivety?.limitFlag && (
                                <p className="flop_times">
                                    {t.rich('remaining_number2', {
                                        code: () => <i className="flop_times_counts">{luckyActivety?.available || 0}</i>,
                                    })}
                                </p>
                            )}
                            {status === 1 && (
                                <EditorLink
                                    className={`flop_btn ${disabledGame ? 'disabled' : ''}`}
                                    onClick={handleStart}
                                    dataLayer={{
                                        event: 'uaEvent',
                                        event_name: 'activity_button',
                                        page_name: pathname,
                                        list_name: 'Flip Cards',
                                        button_name: t('lucky_flop_start'),
                                        function: 'Flip Cards',
                                    }}
                                >
                                    {t('lucky_flop_start')}
                                </EditorLink>
                            )}
                            {status === 3 && (
                                <EditorLink className="flop_btn" onClick={handleAccept}>
                                    {t('lucky_flop_get')}
                                </EditorLink>
                            )}
                        </div>
                    </div>

                    <div className="lucky-right">
                        <FlopGame
                            status={status}
                            disabledGame={disabledGame}
                            flopList={flopList}
                            handleChoose={handleChoose}
                            bgImage={imgConfig.bgImage}
                            frontImage={imgConfig.frontImage}
                            backImage={imgConfig.backImage}
                            kvImage={imgConfig.kvImage}
                            handleStart={handleStart}
                            handleAccept={handleAccept}
                        />
                    </div>
                </div>
            </div>

            {/* 活动规则弹框 */}
            <RuleModal currNotes={currNotes} setCurrNotes={setCurrNotes} />

            {/* 未中奖弹框 */}
            <NoAward
                desc={winnerPrize?.tips}
                open={winnerPrize?.prizeType === 'NOTHING' || winnerPrize?.type === 'NOTHING'}
                onCancel={() => {
                    setWinnerPrize(undefined);
                }}
            />

            {/* 优惠券弹框 */}
            {['THIRD_COUPONS', 'COUPONS', 'INTEGRAL'].includes(winnerPrize?.prizeType) && (
                <CouponModal
                    open={['THIRD_COUPONS', 'COUPONS', 'INTEGRAL'].includes(winnerPrize?.prizeType)}
                    prize={luckyActivety?.drawAwards || []}
                    winnerPrize={winnerPrize}
                    coupon={handleAddNotes(winnerPrize?.rewardRedeemRespVo)}
                    setShowPage={setShowPage}
                    onCancel={() => {
                        setWinnerPrize(undefined);
                    }}
                />
            )}

            {isClient &&
                showLoginModal &&
                createPortal(
                    <LoginFormModal
                        visible={showLoginModal}
                        onCancel={() => {
                            setShowLoginModal(false);
                        }}
                        type="passwordless"
                    />,
                    document.body,
                )}
        </>
    );
};

export default LuckyTurntable;
